import styles from "./styles/SchoolFeedDisplay.module.scss"
import classNames from 'classnames';
import {IFallbackData, ISchoolFeedUrlQueries, isSchoolFeedStandalone} from "../../utils/SchoolBlocksUtilities";
import {useStores} from "../../stores/useStores";
import React, {Suspense} from "react";
import SchoolFeedNewsDisplay, {SchoolFeedNewsLoading} from "./SchoolFeedNewsDisplay";
import SchoolFeedFilesDisplay from "./SchoolFeedFilesDisplay";
import {SchoolFeedMediaDisplay} from "./SchoolFeedMediaDisplay";
import SchoolFeedCourseworkDisplay from "./SchoolFeedCourseworkDisplay";
import {currentDateFormat, currentMonthFormat} from "../utilities/CleverCalendar/utils";
import moment from "moment";
import {useSchoolFeedContentPathQueryParams} from "./hooks/useSchoolFeedInfiniteScroll";
import {currentRangeUrlQueryFormat, getEventsForRange} from "./utils";
import {
    SchoolFeedListCalendar,
} from "./SchoolFeedMonthCalendar";
import {ListCalendar} from "../utilities/CleverCalendar/ListCalendar";
import WatsonApi from "../../backends/WatsonApi";
import Leaderboard from "./leaderboard/Leaderboard";
import {ErrorBoundary} from "../utilities/ErrorBoundary";

export const useInitialNewsPromise = (urlQuery): Promise<{
    contentItems: NewsContentItem[],
    total: number,
}> => {
    const pathQueryParams = useSchoolFeedContentPathQueryParams();

    const q = {
        expand: "organization",
        page: 1,
        is_news: true,
        page_size: 10,
        ...pathQueryParams,
    }

    return new Promise((res, rej) => {
        WatsonApi().then(client => {
            let method;
            if (urlQuery.orgId) {
                method = client.apis.organizations.organizations_schoolfeed_items_list;
            } else {
                method = client.apis.schoolfeed_items.schoolfeed_items_list;
            }

            method({
                organization_pk: urlQuery.orgId,
                ...q,
            }).then(result => {
                res({
                    contentItems: result.obj.data,
                    total: result.obj.meta.total,
                })
            })
        })
    })
}

export function useContributingOrganizationsCountPromise(): Promise<number> {
    const {organizationStore} = useStores();

    return new Promise(async (res, rej) => {
        WatsonApi().then(client => {
            client.apis.organizations.organizations_cleverfeed_directory({
                id: organizationStore.organization.id,
                fields: ["path", "sort_order", "category_name"], // slight optimization since all we need is length
            }).then(result => {
                const parsed = JSON.parse(result.data);
                res(parsed.blocks.filter(b => b.filterCategories).length); // filter out anything without filterCategories as these won't be shown on the directory page
            })
        })
    })
}

export const useGetInitialPromises = (urlQuery) => {
    const {interfaceStore} = useStores();

    const pathQueryParams = useSchoolFeedContentPathQueryParams();

    let initialEventsPromise, initialNewsPromise
    // Note that this is a server component - this breakpoint is NOT RESPONSIVE! This is the server's expectation
    // of screen size based on user agent.
    const showWidgets = interfaceStore.breakpoint === "break-point-lg";

    if (showWidgets || urlQuery.type === 'news') {
        initialNewsPromise = useInitialNewsPromise(urlQuery);
    }
    if (showWidgets || urlQuery.type === 'events') {
        const selectedDateMoment = urlQuery.currentSelectedDate ? moment(urlQuery.currentSelectedDate) : moment();
        initialEventsPromise = getEventsForRange(
            pathQueryParams,
            urlQuery,
            selectedDateMoment.startOf('month').format(currentRangeUrlQueryFormat),
            selectedDateMoment.add(1, 'months').startOf('month').format(currentRangeUrlQueryFormat)
        );
    }
    return {initialNewsPromise, initialEventsPromise}
}

export function SchoolFeedContent(props: {
    urlQuery: ISchoolFeedUrlQueries,
    initialNewsPromise?: Promise<IFallbackData>,
    initialEventsPromise?: Promise<EventContentItem[]>,
}) {
    if (props.urlQuery.currentView === "leaderboard") {
        return <Leaderboard />
    }
    switch (props.urlQuery.type) {
        case "news":
            // @ts-ignore
            return <Suspense fallback={<SchoolFeedNewsLoading />}>
                <SchoolFeedNewsDisplay
                    urlQuery={props.urlQuery} initialNewsPromise={props.initialNewsPromise}
                />
            </Suspense>
        case "events":
            // @ts-ignore
            return <Suspense fallback={<ListCalendar
                currentMonth={moment().format(currentMonthFormat)}
                currentDate={moment().format(currentDateFormat)}
                events={[]}
                isLoading={true}
            />}>
                <SchoolFeedListCalendar initialEventsPromise={props.initialEventsPromise} />
            </Suspense>
        case "files":
            return <SchoolFeedFilesDisplay
                urlQuery={props.urlQuery}
            />
        case "media":
            return <SchoolFeedMediaDisplay
                urlQuery={props.urlQuery}
            />
        case "coursework":
            return <SchoolFeedCourseworkDisplay
                urlQuery={props.urlQuery}
            />
    }
}

const SchoolFeedDisplay = (props: {
    urlQuery: ISchoolFeedUrlQueries,
}) => {
    const displayContainerClassName = classNames({
        [styles.displayContainer]: true,
        [styles.displayContainerStandalone]: isSchoolFeedStandalone(),
        [styles.displayContainerFiles]: props.urlQuery.type === 'files',
    });

    const {initialNewsPromise, initialEventsPromise} = useGetInitialPromises(props.urlQuery);

    return (
        <div className={displayContainerClassName} data-current-view={props.urlQuery.currentView}>
            <ErrorBoundary>
                <SchoolFeedContent
                    key={props.urlQuery.type} // Keep key to force remount and show fallback during transitions
                    urlQuery={props.urlQuery}
                    initialEventsPromise={initialEventsPromise}
                    initialNewsPromise={initialNewsPromise}
                />
            </ErrorBoundary>
        </div>
    );
}

export default SchoolFeedDisplay;
